/**
 * TemplateManagement.js - Admin Schedule Templates
 * 
 * Allows admins to:
 * - View all saved schedule templates
 * - Create template from current week's schedule
 * - Preview and apply templates to future weeks
 * - Edit template name/description
 * - Delete templates
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import dataStore from '../data/dataStore';

// Helper to get Monday of the current week
const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date.toISOString().split('T')[0];
};

// Helper to format date for display
const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Helper to format week range
const formatWeekRange = (mondayStr) => {
  const monday = new Date(mondayStr + 'T12:00:00');
  const friday = new Date(monday);
  friday.setDate(friday.getDate() + 4);
  return `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${friday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

function TemplateManagement() {
  const { theme, colors, darkMode } = useTheme();
  const { user: currentUser } = useAuth();
  
  const [templates, setTemplates] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Create from week modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createWeekStart, setCreateWeekStart] = useState(getMonday(new Date()));
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  
  // Apply template modal
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyTemplate, setApplyTemplate] = useState(null);
  const [applyTargetWeek, setApplyTargetWeek] = useState(getMonday(new Date()));
  const [applyOptions, setApplyOptions] = useState({
    skipPTO: true,
    overwriteExisting: false,
    respectAvailability: true
  });
  const [applyPreview, setApplyPreview] = useState(null);
  
  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  
  // Preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  // Messages
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Load data
  const loadData = () => {
    setTemplates(dataStore.getTemplates());
    setRooms(dataStore.getRooms());
    setUsers(dataStore.getUsers());
  };

  useEffect(() => {
    loadData();
  }, []);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // Get room name by ID
  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room?.name || 'Unknown Room';
  };

  // Get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown User';
  };

  // Handle creating template from week
  const handleCreateFromWeek = async () => {
    if (!createName.trim()) {
      setError('Please enter a template name');
      return;
    }
    
    try {
      const template = await dataStore.createTemplateFromWeek(
        createWeekStart,
        createName.trim(),
        createDescription.trim(),
        currentUser
      );
      
      const stats = dataStore.getTemplateStats(template.id);
      if (stats.totalAssignments === 0) {
        setError('No schedule assignments found for that week');
        await dataStore.deleteTemplate(template.id, currentUser);
        return;
      }
      
      setMessage(`Template "${template.name}" created with ${stats.totalAssignments} assignments`);
      setShowCreateModal(false);
      setCreateName('');
      setCreateDescription('');
      loadData();
    } catch (err) {
      console.error('Error creating template:', err);
      setError(err.message || 'Failed to create template');
    }
  };

  // Open apply modal and generate preview
  const openApplyModal = (template) => {
    setApplyTemplate(template);
    setApplyTargetWeek(getMonday(new Date()));
    setApplyOptions({ skipPTO: true, overwriteExisting: false, respectAvailability: true });
    setApplyPreview(null);
    setShowApplyModal(true);
  };

  // Generate preview when target week changes
  const generatePreview = () => {
    if (applyTemplate) {
      const preview = dataStore.previewTemplateApplication(applyTemplate.id, applyTargetWeek);
      setApplyPreview(preview);
    }
  };

  useEffect(() => {
    if (showApplyModal && applyTemplate) {
      generatePreview();
    }
  }, [applyTargetWeek, showApplyModal, applyTemplate]);

  // Handle applying template
  const handleApplyTemplate = async () => {
    if (!applyTemplate) return;
    
    try {
      const result = await dataStore.applyTemplate(
        applyTemplate.id,
        applyTargetWeek,
        applyOptions,
        currentUser
      );
      
      if (result.success) {
        setMessage(`Applied ${result.createdCount} assignments (${result.skippedCount} skipped)`);
        setShowApplyModal(false);
        setApplyTemplate(null);
        setApplyPreview(null);
      } else {
        setError(result.error || 'Failed to apply template');
      }
    } catch (err) {
      console.error('Error applying template:', err);
      setError(err.message || 'Failed to apply template');
    }
  };

  // Open edit modal
  const openEditModal = (template) => {
    setEditTemplate(template);
    setEditName(template.name);
    setEditDescription(template.description || '');
    setShowEditModal(true);
  };

  // Handle editing template
  const handleEditTemplate = async () => {
    if (!editTemplate || !editName.trim()) return;
    
    try {
      await dataStore.updateTemplate(editTemplate.id, {
        name: editName.trim(),
        description: editDescription.trim()
      }, currentUser);
      
      setMessage('Template updated');
      setShowEditModal(false);
      setEditTemplate(null);
      loadData();
    } catch (err) {
      console.error('Error updating template:', err);
      setError(err.message || 'Failed to update template');
    }
  };

  // Handle deleting template
  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await dataStore.deleteTemplate(template.id, currentUser);
      setMessage('Template deleted');
      loadData();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err.message || 'Failed to delete template');
    }
  };

  // Open preview modal
  const openPreviewModal = (template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  // ============================================================================
  // STYLES
  // ============================================================================

  const containerStyle = {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const headerCardStyle = {
    backgroundColor: theme.cardBackground,
    padding: '16px 20px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px'
  };

  const titleStyle = {
    fontSize: '1.4rem',
    fontWeight: '700',
    color: theme.text,
    margin: 0
  };

  const buttonStyle = {
    padding: '10px 20px',
    backgroundColor: colors.blue,
    color: darkMode ? '#fff' : '#000',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.backgroundSecondary,
    color: theme.text,
    border: `1px solid ${theme.border}`
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: colors.red,
    padding: '8px 16px',
    fontSize: '0.85rem'
  };

  const cardStyle = {
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${theme.border}`
  };

  const templateNameStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: theme.text,
    marginBottom: '4px'
  };

  const templateDescStyle = {
    fontSize: '0.9rem',
    color: theme.textSecondary,
    marginBottom: '12px'
  };

  const templateMetaStyle = {
    fontSize: '0.85rem',
    color: theme.textSecondary,
    marginBottom: '16px'
  };

  const templateActionsStyle = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  };

  const actionButtonStyle = {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '500'
  };

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  };

  const modalStyle = {
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    padding: '24px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const modalTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: theme.text,
    marginBottom: '20px'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    fontSize: '0.95rem',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '500',
    color: theme.text,
    marginBottom: '6px'
  };

  const formGroupStyle = {
    marginBottom: '16px'
  };

  const messageStyle = {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '0.95rem'
  };

  const successStyle = {
    ...messageStyle,
    backgroundColor: darkMode ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5',
    color: darkMode ? '#34d399' : '#065f46'
  };

  const errorStyle = {
    ...messageStyle,
    backgroundColor: darkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
    color: darkMode ? '#f87171' : '#991b1b'
  };

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '60px 20px',
    color: theme.textSecondary
  };

  const statBadgeStyle = {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
    backgroundColor: darkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
    color: colors.blue,
    marginRight: '8px'
  };

  const checkboxLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem',
    color: theme.text,
    cursor: 'pointer',
    marginBottom: '8px'
  };

  const warningBoxStyle = {
    backgroundColor: darkMode ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7',
    border: `1px solid ${darkMode ? '#f59e0b' : '#f59e0b'}`,
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px'
  };

  const warningTextStyle = {
    fontSize: '0.85rem',
    color: darkMode ? '#fcd34d' : '#92400e',
    margin: 0
  };


  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerCardStyle}>
        <h1 style={titleStyle}>📋 Schedule Templates</h1>
        <button
          style={buttonStyle}
          onClick={() => setShowCreateModal(true)}
        >
          + Create Template
        </button>
      </div>

      {/* Messages */}
      {message && <div style={successStyle}>{message}</div>}
      {error && <div style={errorStyle}>{error}</div>}

      {/* Template List */}
      {templates.length === 0 ? (
        <div style={emptyStateStyle}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📋</div>
          <h3 style={{ color: theme.text, marginBottom: '8px' }}>No Templates Yet</h3>
          <p>Create a template from an existing week's schedule to quickly apply it to future weeks.</p>
        </div>
      ) : (
        templates.map(template => {
          const stats = dataStore.getTemplateStats(template.id);
          return (
            <div key={template.id} style={cardStyle}>
              <div style={templateNameStyle}>{template.name}</div>
              {template.description && (
                <div style={templateDescStyle}>{template.description}</div>
              )}
              <div style={templateMetaStyle}>
                Created: {template.createdAt ? formatDate(template.createdAt.split('T')[0]) : 'Unknown'}
                {template.updatedAt && template.updatedAt !== template.createdAt && (
                  <> · Updated: {formatDate(template.updatedAt.split('T')[0])}</>
                )}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <span style={statBadgeStyle}>{stats?.totalAssignments || 0} assignments</span>
                <span style={statBadgeStyle}>{stats?.roomsCovered || 0} rooms</span>
                <span style={statBadgeStyle}>{stats?.teachersIncluded || 0} teachers</span>
                <span style={statBadgeStyle}>{stats?.daysWithAssignments || 0} days</span>
              </div>
              <div style={templateActionsStyle}>
                <button
                  style={{ ...actionButtonStyle, backgroundColor: colors.blue, color: darkMode ? '#fff' : '#000' }}
                  onClick={() => openApplyModal(template)}
                >
                  Apply to Week
                </button>
                <button
                  style={{ ...actionButtonStyle, backgroundColor: theme.backgroundSecondary, color: theme.text }}
                  onClick={() => openPreviewModal(template)}
                >
                  Preview
                </button>
                <button
                  style={{ ...actionButtonStyle, backgroundColor: theme.backgroundSecondary, color: theme.text }}
                  onClick={() => openEditModal(template)}
                >
                  Edit
                </button>
                <button
                  style={{ ...actionButtonStyle, backgroundColor: colors.red, color: darkMode ? '#fff' : '#000' }}
                  onClick={() => handleDeleteTemplate(template)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div style={modalOverlayStyle} onClick={() => setShowCreateModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Create Template from Week</h2>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>Source Week</label>
              <input
                type="date"
                style={inputStyle}
                value={createWeekStart}
                onChange={e => {
                  const monday = getMonday(new Date(e.target.value + 'T12:00:00'));
                  setCreateWeekStart(monday);
                }}
              />
              <div style={{ fontSize: '0.85rem', color: theme.textSecondary, marginTop: '4px' }}>
                Week of {formatWeekRange(createWeekStart)}
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Template Name *</label>
              <input
                type="text"
                style={inputStyle}
                value={createName}
                onChange={e => setCreateName(e.target.value)}
                placeholder="e.g., Standard Week, Summer Schedule"
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Description (optional)</label>
              <textarea
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                value={createDescription}
                onChange={e => setCreateDescription(e.target.value)}
                placeholder="Brief description of this schedule pattern..."
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                style={secondaryButtonStyle}
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button
                style={buttonStyle}
                onClick={handleCreateFromWeek}
              >
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Template Modal */}
      {showApplyModal && applyTemplate && (
        <div style={modalOverlayStyle} onClick={() => setShowApplyModal(false)}>
          <div style={{ ...modalStyle, maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Apply Template: {applyTemplate.name}</h2>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>Target Week</label>
              <input
                type="date"
                style={inputStyle}
                value={applyTargetWeek}
                onChange={e => {
                  const monday = getMonday(new Date(e.target.value + 'T12:00:00'));
                  setApplyTargetWeek(monday);
                }}
              />
              <div style={{ fontSize: '0.85rem', color: theme.textSecondary, marginTop: '4px' }}>
                Week of {formatWeekRange(applyTargetWeek)}
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Options</label>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={applyOptions.skipPTO}
                  onChange={e => setApplyOptions({ ...applyOptions, skipPTO: e.target.checked })}
                />
                Skip days where teacher has PTO
              </label>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={applyOptions.respectAvailability}
                  onChange={e => setApplyOptions({ ...applyOptions, respectAvailability: e.target.checked })}
                />
                Respect teacher availability windows
              </label>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={applyOptions.overwriteExisting}
                  onChange={e => setApplyOptions({ ...applyOptions, overwriteExisting: e.target.checked })}
                />
                Overwrite existing assignments
              </label>
            </div>

            {/* Preview Section */}
            {applyPreview && (
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Preview</label>
                
                {applyPreview.skippedPTO.length > 0 && (
                  <div style={warningBoxStyle}>
                    <p style={warningTextStyle}>
                      <strong>⚠️ PTO Conflicts:</strong> {applyPreview.skippedPTO.length} assignment(s) will be skipped due to PTO
                    </p>
                    <ul style={{ ...warningTextStyle, margin: '8px 0 0 20px', padding: 0 }}>
                      {applyPreview.skippedPTO.slice(0, 5).map((s, i) => (
                        <li key={i}>{s.userName} on {s.dayName}</li>
                      ))}
                      {applyPreview.skippedPTO.length > 5 && (
                        <li>...and {applyPreview.skippedPTO.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {applyPreview.conflicts.length > 0 && !applyOptions.overwriteExisting && (
                  <div style={warningBoxStyle}>
                    <p style={warningTextStyle}>
                      <strong>⚠️ Existing Assignments:</strong> {applyPreview.conflicts.length} teacher(s) already have assignments that week
                    </p>
                    <ul style={{ ...warningTextStyle, margin: '8px 0 0 20px', padding: 0 }}>
                      {applyPreview.conflicts.slice(0, 5).map((c, i) => (
                        <li key={i}>{c.userName} on {c.dayName}</li>
                      ))}
                      {applyPreview.conflicts.length > 5 && (
                        <li>...and {applyPreview.conflicts.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}

                <div style={{ 
                  backgroundColor: theme.backgroundSecondary, 
                  borderRadius: '8px', 
                  padding: '12px',
                  fontSize: '0.9rem',
                  color: theme.text
                }}>
                  <strong>Will create:</strong> {applyPreview.totalAssignments - (applyOptions.skipPTO ? applyPreview.skippedPTO.length : 0) - (!applyOptions.overwriteExisting ? applyPreview.conflicts.length : 0)} assignments
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                style={secondaryButtonStyle}
                onClick={() => {
                  setShowApplyModal(false);
                  setApplyTemplate(null);
                  setApplyPreview(null);
                }}
              >
                Cancel
              </button>
              <button
                style={buttonStyle}
                onClick={handleApplyTemplate}
              >
                Apply Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {showEditModal && editTemplate && (
        <div style={modalOverlayStyle} onClick={() => setShowEditModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Edit Template</h2>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>Template Name *</label>
              <input
                type="text"
                style={inputStyle}
                value={editName}
                onChange={e => setEditName(e.target.value)}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Description</label>
              <textarea
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                style={secondaryButtonStyle}
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                style={buttonStyle}
                onClick={handleEditTemplate}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Template Modal */}
      {showPreviewModal && previewTemplate && (
        <div style={modalOverlayStyle} onClick={() => setShowPreviewModal(false)}>
          <div style={{ ...modalStyle, maxWidth: '800px' }} onClick={e => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>Template Preview: {previewTemplate.name}</h2>
            
            {previewTemplate.description && (
              <p style={{ color: theme.textSecondary, marginBottom: '20px' }}>
                {previewTemplate.description}
              </p>
            )}

            {/* Day-by-day breakdown */}
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((dayName, dayIndex) => {
              const dayAssignments = previewTemplate.assignments[dayIndex];
              if (!dayAssignments || Object.keys(dayAssignments).length === 0) {
                return null;
              }
              
              return (
                <div key={dayIndex} style={{ marginBottom: '20px' }}>
                  <h4 style={{ 
                    color: theme.text, 
                    fontSize: '1rem', 
                    fontWeight: '600',
                    marginBottom: '8px',
                    borderBottom: `1px solid ${theme.border}`,
                    paddingBottom: '4px'
                  }}>
                    {dayName}
                  </h4>
                  
                  {Object.keys(dayAssignments).map(roomId => {
                    const roomAssigns = dayAssignments[roomId];
                    return (
                      <div key={roomId} style={{ marginBottom: '8px', marginLeft: '12px' }}>
                        <span style={{ 
                          fontWeight: '500', 
                          color: theme.text,
                          display: 'inline-block',
                          width: '120px'
                        }}>
                          {getRoomName(roomId)}:
                        </span>
                        <span style={{ color: theme.textSecondary }}>
                          {roomAssigns.map((a, i) => (
                            <span key={i}>
                              {i > 0 && ', '}
                              {getUserName(a.userId)} ({a.startTime}-{a.endTime})
                            </span>
                          ))}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                style={secondaryButtonStyle}
                onClick={() => setShowPreviewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplateManagement;
